import axios from "axios";
import {
  Package as PackageIcon,
  Pencil,
  Plus,
  Trash2
} from "lucide-react";
import { useEffect, useState } from "react";
import Breadcrumb from "../../../components/AdminSideComponents/BreadCrumb";
import PackageDeleteConfirm from "../../../components/AdminSideComponents/packages/PackageDeleteConfirm";
import PackageFormModal from "../../../components/AdminSideComponents/packages/PackageFormModal";
import { showError } from "../../../utils/toastUtils"; // optional - if you have toast

const AdminPackages = () => {
  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(true);

  // Modal states
  const [createOpen, setCreateOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [selectedPackage, setSelectedPackage] = useState(null);

  // Fetch all packages
  const fetchPackages = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${process.env.REACT_APP_API_URL}/packages`);
      setPackages(res.data.data || res.data || []);
    } catch (err) {
      console.error("Failed to fetch packages:", err);
      showError?.("Failed to load packages") || alert("Failed to load packages");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPackages();
  }, []);

  const handleSuccess = () => {
    fetchPackages();
  };

  const handleCreate = () => {
    setSelectedPackage(null);
    setCreateOpen(true);
  };

  const handleEdit = (pkg) => {
    setSelectedPackage(pkg);
    setEditOpen(true);
  };

  // Open delete confirmation
  const handleDeleteClick = (pkg) => {
    setSelectedPackage(pkg);
    setDeleteOpen(true);
  };

  // Confirm delete
  const handleDelete = async () => {
    if (!selectedPackage) return;
    try {
      await axios.delete(
        `${process.env.REACT_APP_API_URL}/packages/${selectedPackage._id}`
      );
      handleSuccess();
      setDeleteOpen(false);
      setSelectedPackage(null);
    } catch (err) {
      console.error(err);
      showError?.(err?.response?.data?.message || "Failed to delete package") ||
        alert(err?.response?.data?.message || "Failed to delete package");
    }
  };

  return (
    <div className="bg-gradient-to-br from-[#FFF7F3] to-gray-50 min-h-screen px-6">
      {/* Header / Breadcrumb */}
      <div className="pt-3">
        <Breadcrumb
          title1="Packages"
          title2="Manage Packages"
          showBtn={true}
          buttonName="Add New Package"
          onBtnClick={() => handleCreate()}
          buttonIcon={<Plus size={18} />}
        />
      </div>

      {/* Main Content */}
      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {[...Array(6)].map((_, i) => (
            <div
              key={i}
              className="h-72 rounded-xl bg-gray-200 animate-pulse shadow-md"
            />
          ))}
        </div>
      ) : packages.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-24 text-center">
          <div className="w-24 h-24 rounded-full bg-gray-100 flex items-center justify-center mb-6 shadow-sm">
            <PackageIcon className="w-12 h-12 text-gray-400" />
          </div>
          <h3 className="text-2xl font-semibold text-gray-800 mb-3">
            No packages yet
          </h3>
          <p className="text-gray-500 max-w-md text-lg">
            You haven't created any service packages. Add your first package to
            start offering bundled services.
          </p>
          <button
            onClick={() => handleCreate()}
            className="mt-6 px-6 py-3 bg-orange-500 hover:bg-orange-600 text-white rounded-lg font-medium transition flex items-center gap-2 shadow-md"
          >
            <Plus size={20} />
            Create First Package
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 pb-12">
          {packages.map((pkg) => (
            <div
              key={pkg._id}
              className="group relative bg-[#f3e7d9] rounded-md overflow-hidden shadow-lg hover:shadow-2xl transition duration-300 border border-[#e5d5c5]"
            >
              {/* Top Image */}
              <div className="relative">
                {pkg.banner ? (
                  <img
                    src={pkg.banner}
                    alt={pkg.name}
                    className="h-60 w-full object-cover"
                  />
                ) : (
                  <div className="h-60 w-full bg-gray-200 flex items-center justify-center">
                    <PackageIcon size={60} className="text-gray-400" />
                  </div>
                )}

                {/* Status badge */}
                <div className="absolute top-3 right-3">
                  <span
                    className={`px-3 py-1 text-xs font-semibold rounded-full shadow-md ${pkg.isActive
                      ? "bg-green-600 text-white"
                      : "bg-red-600 text-white"
                      }`}
                  >
                    {pkg.isActive ? "Active" : "Inactive"}
                  </span>
                </div>
              </div>

              {/* Package Title Strip */}
              <div className="bg-[#5a2d1e] py-4 text-center px-3">
                <h3 className="text-white flex items-center justify-center gap-2 text-xl font-semibold tracking-wide uppercase">
                  {pkg.name} <span className="text-xs bg-primaryColor/20 inline-block rounded-full px-2 py-1 text-primaryColor font-normal">{pkg.category}</span>
                </h3>
              </div>

              {/* Services Section */}
              <div className="bg-[#5a2d1e] text-white px-6 py-5 min-h-[200px] h-[calc(100%-369px)]">
                <ul className="list-disc list-inside space-y-2 text-sm">
                  {pkg.services && pkg.services.length > 0 ? (
                    pkg.services.map((serviceName, index) => (
                      <li key={index}>{serviceName}</li>
                    ))
                  ) : (
                    <li>No services added</li>
                  )}
                </ul>
              </div>

              {/* Price Section */}
              <div className="bg-[#e8d7c6] py-4 text-center border-t border-[#d6c3b2]">
                {pkg.discountedPrice && pkg.discountedPrice < pkg.price ? (
                  <div>
                    <span className="text-gray-600 line-through mr-2 text-lg">
                      ₹{pkg.price.toFixed(0)}
                    </span>
                    <span className="text-3xl font-bold text-[#5a2d1e]">
                      ₹{pkg.discountedPrice.toFixed(0)}
                    </span>
                  </div>
                ) : (
                  <span className="text-3xl font-bold text-[#5a2d1e]">
                    ₹{pkg.price.toFixed(0)}
                  </span>
                )}
              </div>

              {/* Hover Edit/Delete */}
              <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition duration-300 flex items-center justify-center gap-6">
                <button
                  onClick={() => handleEdit(pkg)}
                  className="bg-primaryColor p-4 rounded-full hover:bg-primaryBtnHoverColor transition"
                >
                  <Pencil className="w-6 h-6 text-white" />
                </button>

                <button
                  onClick={() => handleDeleteClick(pkg)}
                  className="bg-red-600 p-4 rounded-full hover:bg-red-700 transition"
                >
                  <Trash2 className="w-6 h-6 text-white" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Modals */}

      <PackageFormModal
        isOpen={createOpen || editOpen}
        onClose={() => {
          setCreateOpen(false);
          setEditOpen(false);
          setSelectedPackage(null);
        }}
        packageData={selectedPackage}
        onSuccess={handleSuccess}
      />

      <PackageDeleteConfirm
        isOpen={deleteOpen}
        onClose={() => {
          setDeleteOpen(false);
          setSelectedPackage(null);
        }}
        packageData={selectedPackage}
        onConfirm={handleDelete}
      />
    </div>
  );
};

export default AdminPackages;




// import axios from "axios";
// import { useEffect, useState } from "react";
// import {
//   Package as PackageIcon,
//   Pencil,
//   Trash2,
//   Plus,
//   IndianRupee,
//   Tag,
//   FileText,
//   Loader2,
// } from "lucide-react";
// import Breadcrumb from "../../../components/AdminSideComponents/BreadCrumb";
// import { showError } from "../../../utils/toastUtils"; // optional - if you have toast
// import PackageDeleteConfirm from "../../../components/AdminSideComponents/packages/PackageDeleteConfirm";
// import PackageFormModal from "../../../components/AdminSideComponents/packages/PackageFormModal";

// const AdminPackages = () => {
//   const [packages, setPackages] = useState([]);
//   const [loading, setLoading] = useState(true);

//   // Modal states
//   const [createOpen, setCreateOpen] = useState(false);
//   const [editOpen, setEditOpen] = useState(false);
//   const [deleteOpen, setDeleteOpen] = useState(false);
//   const [selectedPackage, setSelectedPackage] = useState(null);

//   // Fetch all packages
//   const fetchPackages = async () => {
//     try {
//       setLoading(true);
//       const res = await axios.get(`${process.env.REACT_APP_API_URL}/packages`);
//       setPackages(res.data.data || res.data || []);
//     } catch (err) {
//       console.error("Failed to fetch packages:", err);
//       showError?.("Failed to load packages") || alert("Failed to load packages");
//     } finally {
//       setLoading(false);
//     }
//   };

//   useEffect(() => {
//     fetchPackages();
//   }, []);

//   const handleSuccess = () => {
//     fetchPackages();
//   };

//   const handleCreate = () => {
//     setSelectedPackage(null);
//     setCreateOpen(true);
//   };

//   const handleEdit = (pkg) => {
//     setSelectedPackage(pkg);
//     setEditOpen(true);
//   };

//   // Open delete confirmation
//   const handleDeleteClick = (pkg) => {
//     setSelectedPackage(pkg);
//     setDeleteOpen(true);
//   };

//   // Confirm delete
//   const handleDelete = async () => {
//     if (!selectedPackage) return;
//     try {
//       await axios.delete(
//         `${process.env.REACT_APP_API_URL}/packages/${selectedPackage._id}`
//       );
//       handleSuccess();
//       setDeleteOpen(false);
//       setSelectedPackage(null);
//     } catch (err) {
//       console.error(err);
//       showError?.(err?.response?.data?.message || "Failed to delete package") ||
//         alert(err?.response?.data?.message || "Failed to delete package");
//     }
//   };

//   return (
//     <div className="bg-gradient-to-br from-[#FFF7F3] to-gray-50 min-h-screen px-6">
//       {/* Header / Breadcrumb */}
//       <div className="pt-3">
//         <Breadcrumb
//           title1="Packages"
//           title2="Manage Packages"
//           showBtn={true}
//           buttonName="Add New Package"
//           onBtnClick={() => handleCreate()}
//           buttonIcon={<Plus size={18} />}
//         />
//       </div>

//       {/* Main Content */}
//       {loading ? (
//         <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
//           {[...Array(6)].map((_, i) => (
//             <div
//               key={i}
//               className="h-72 rounded-xl bg-gray-200 animate-pulse shadow-md"
//             />
//           ))}
//         </div>
//       ) : packages.length === 0 ? (
//         <div className="flex flex-col items-center justify-center py-24 text-center">
//           <div className="w-24 h-24 rounded-full bg-gray-100 flex items-center justify-center mb-6 shadow-sm">
//             <PackageIcon className="w-12 h-12 text-gray-400" />
//           </div>
//           <h3 className="text-2xl font-semibold text-gray-800 mb-3">
//             No packages yet
//           </h3>
//           <p className="text-gray-500 max-w-md text-lg">
//             You haven’t created any service packages. Add your first package to
//             start offering bundled services.
//           </p>
//           <button
//             onClick={() => handleCreate()}
//             className="mt-6 px-6 py-3 bg-orange-500 hover:bg-orange-600 text-white rounded-lg font-medium transition flex items-center gap-2 shadow-md"
//           >
//             <Plus size={20} />
//             Create First Package
//           </button>
//         </div>
//       ) : (
//         <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 pb-12">
//           {packages.map((pkg) => (
//             <div
//               key={pkg._id}
//               className="group relative bg-[#f3e7d9] rounded-md overflow-hidden shadow-lg hover:shadow-2xl transition duration-300 border border-[#e5d5c5]"
//             >
//               {/* Top Image */}
//               <div className="relative">
//                 {pkg.banner ? (
//                   <img
//                     src={pkg.banner}
//                     alt={pkg.name}
//                     className="h-60 w-full object-cover"
//                   />
//                 ) : (
//                   <div className="h-60 w-full bg-gray-200 flex items-center justify-center">
//                     <PackageIcon size={60} className="text-gray-400" />
//                   </div>
//                 )}

//                 {/* Status badge */}
//                 <div className="absolute top-3 right-3">
//                   <span
//                     className={`px-3 py-1 text-xs font-semibold rounded-full shadow-md ${pkg.isActive
//                       ? "bg-green-600 text-white"
//                       : "bg-red-600 text-white"
//                       }`}
//                   >
//                     {pkg.isActive ? "Active" : "Inactive"}
//                   </span>
//                 </div>
//               </div>

//               {/* Package Title Strip */}
//               <div className="bg-[#5a2d1e] py-4 text-center">
//                 <h3 className="text-white text-xl font-semibold tracking-wide uppercase">
//                   {pkg.name}
//                 </h3>
//               </div>

//               {/* Services Section */}
//               <div className="bg-[#5a2d1e] text-white px-6 py-5 min-h-[200px] h-[calc(100%-369px)]">
//                 <ul className="list-disc list-inside space-y-2 text-sm">
//                   {pkg.services && pkg.services.length > 0 ? (
//                     pkg.services.map((service, index) => (
//                       <li key={index}>{service?.name || "Service"}</li>
//                     ))
//                   ) : (
//                     <li>No services added</li>
//                   )}
//                 </ul>
//               </div>

//               {/* Price Section */}
//               <div className="bg-[#e8d7c6] py-4 text-center border-t border-[#d6c3b2]">
//                 {pkg.discountedPrice && pkg.discountedPrice < pkg.price ? (
//                   <div>
//                     <span className="text-gray-600 line-through mr-2 text-lg">
//                       ₹{pkg.price.toFixed(0)}
//                     </span>
//                     <span className="text-3xl font-bold text-[#5a2d1e]">
//                       ₹{pkg.discountedPrice.toFixed(0)}
//                     </span>
//                   </div>
//                 ) : (
//                   <span className="text-3xl font-bold text-[#5a2d1e]">
//                     ₹{pkg.price.toFixed(0)}
//                   </span>
//                 )}
//               </div>

//               {/* Hover Edit/Delete */}
//               <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition duration-300 flex items-center justify-center gap-6">
//                 <button
//                   onClick={() => handleEdit(pkg)}
//                   className="bg-primaryColor p-4 rounded-full hover:bg-primaryBtnHoverColor transition"
//                 >
//                   <Pencil className="w-6 h-6 text-white" />
//                 </button>

//                 <button
//                   onClick={() => handleDeleteClick(pkg)}
//                   className="bg-red-600 p-4 rounded-full hover:bg-red-700 transition"
//                 >
//                   <Trash2 className="w-6 h-6 text-white" />
//                 </button>
//               </div>
//             </div>
//           ))}
//         </div>
//       )}

//       {/* Modals */}

//       <PackageFormModal
//         isOpen={createOpen || editOpen}
//         onClose={() => {
//           setCreateOpen(false);
//           setEditOpen(false);
//           setSelectedPackage(null);
//         }}
//         packageData={selectedPackage}
//         onSuccess={handleSuccess}
//       />

//       <PackageDeleteConfirm
//         isOpen={deleteOpen}
//         onClose={() => {
//           setDeleteOpen(false);
//           setSelectedPackage(null);
//         }}
//         packageData={selectedPackage}
//         onConfirm={handleDelete}
//       />
//     </div>
//   );
// };

// export default AdminPackages;